import React, { useState, useEffect } from 'react';
import { Breadcrumb, Select, Switch } from 'antd';
import { HomeOutlined, SaveOutlined } from '@ant-design/icons';
import { Link, useParams } from 'react-router-dom';
import Bread from 'modules/layoutModule/components/Bread/Bread';
import ActivitySwitch from 'modules/layoutModule/components/ActivitySwitch/ActivitySwitch';
import {
  Form,
  Input,
  Button,
  Space,
  Skeleton,
  ConfigProvider,
  BackTop,
} from 'antd';
import { FieldData } from 'rc-field-form/lib/interface';
import { useHistory } from 'react-router-dom';
import plPL from 'antd/lib/locale/pl_PL';
import {
  getSpecificRepresentativesRequest,
  saveRepresentativesRequest,
} from 'modules/contactModule/requests/contactRequests';
import 'moment/locale/pl';
import FormCoverUpload from 'modules/galleryModules/components/FormCoverUpload';
import { getProvincesRequest } from 'modules/sectionsModule/requests/sectionsRequests';

const RepresentativesEdit = (): JSX.Element => {
  const { id } = useParams<{ id: string }>();
  const [form] = Form.useForm();
  const [fields, setFields] = useState<FieldData[]>([]);
  const [loading, toggleLoading] = useState<boolean>(false);
  const history = useHistory();
  const [isSaveButtonLoading, toggleButtonLoading] = useState(false);
  const [isActivityDisabled, toggleActivityDisable] = useState(false);
  const [fileImage1, setFileImage1] = useState<string | null>(null);
  const [fileImage2, setFileImage2] = useState<string | null>(null);
  const [fileImage1Changed, changedFileImage1] = useState<boolean>(false);
  const [fileImage2Changed, changedFileImage2] = useState<boolean>(false);
  const [provinces, setProvinces] = useState([]);

  useEffect(() => {
    toggleLoading(true);

    getProvincesRequest().then((response) => {
      if (response) {
        const sections = response.map(
          (section: { name: string; id: number }) => {
            return {
              label: section.name,
              value: section.id,
            };
          }
        );
        sections.unshift({ label: '', value: '' });
        setProvinces(sections);
      }
    });

    getSpecificRepresentativesRequest(id)
      .then((response) => {
        if (response) {
          toggleLoading(false);
          const { data } = response;
          setFields([
            {
              name: ['id'],
              value: data.id,
            },
            {
              name: ['name'],
              value: data.name,
            },
            {
              name: ['idProvince1'],
              value: data.idProvince1,
            },
            {
              name: ['idProvince2'],
              value: data.idProvince2,
            },
            {
              name: ['idProvince3'],
              value: data.idProvince3,
            },
            {
              name: ['isNews'],
              value: data.isNews,
            },
            {
              name: ['fileImage1'],
              value: data.fileImage1,
            },
            {
              name: ['fileImage2'],
              value: data.fileMap1,
            },
            {
              name: ['phone'],
              value: data.phone,
            },
            {
              name: ['email'],
              value: data.email,
            },
            {
              name: ['sortOrder'],
              value: data.sortOrder,
            },
            {
              name: ['isActive'],
              value: data.isActive || false,
            },
          ]);
          form.setFieldsValue({
            fileImage1: data?.fileImage1,
            fileImage2: data?.fileImage2,
          });
          setFileImage1(data?.fileImage1);
          setFileImage2(data?.fileImage2);
          if (data.activitySwitchDisabled) {
            toggleActivityDisable(true);
          }
        } else {
          history.push('/representatives');
        }
      })
      .catch(() => {
        history.push('/representatives');
      });
  }, []);

  const onFinish = (values: any) => {
    toggleButtonLoading(true);
    saveRepresentativesRequest(values).then(() => {
      toggleButtonLoading(false);
    });
  };

  const validateMessages = {
    required: 'Proszę uzupełnić pole ${label}!',
    types: {
      email: '${label} is not a valid email!',
      number: '${label} is not a valid number!',
    },
    number: {
      range: '${label} must be between ${min} and ${max}',
    },
  };

  const updateCover = (
    e: string,
    field: string,
    coverMethod: (value: string) => void,
    coverMethodChanged: (value: boolean) => void
  ) => {
    setFields([
      {
        name: [field],
        value: e,
      },
    ]);
    coverMethod(e);
    coverMethodChanged(true);
  };

  return (
    <>
      <Bread>
        <>
          <Breadcrumb.Item>
            <HomeOutlined />
          </Breadcrumb.Item>
          <Breadcrumb.Item>Aktualności</Breadcrumb.Item>
          <Breadcrumb.Item>
            <Link to="/representatives">Kategorie</Link>
          </Breadcrumb.Item>
          <Breadcrumb.Item>
            <Link to={`/representatives/${id}`}>Edycja wpisu</Link>
          </Breadcrumb.Item>
        </>
      </Bread>
      <ConfigProvider locale={plPL}>
        <div className="site-layout-background">
          {loading && <Skeleton active />}
          {!loading && (
            <Form
              layout="vertical"
              fields={fields}
              onFinish={onFinish}
              form={form}
              validateMessages={validateMessages}
            >
              <Form.Item name="id" hidden>
                <Input />
              </Form.Item>
              <Form.Item
                name="fileImage1"
                label="Avatar 543x547px"
                rules={[
                  {
                    required: true,
                  },
                ]}
              >
                <FormCoverUpload
                  cover={fileImage1}
                  setCover={(e) =>
                    updateCover(
                      e,
                      'fileImage1',
                      setFileImage1,
                      changedFileImage1
                    )
                  }
                  isNew={fileImage1Changed}
                  id={form.getFieldsValue()?.id}
                  field="fileImage1"
                  number={1}
                  hideTags
                  dontResize
                />
              </Form.Item>
              <Form.Item name="fileImage2" label="Grafika mapki">
                <FormCoverUpload
                  cover={fileImage2}
                  setCover={(e) =>
                    updateCover(
                      e,
                      'fileImage2',
                      setFileImage2,
                      changedFileImage2
                    )
                  }
                  isNew={fileImage2Changed}
                  id={form.getFieldsValue()?.id}
                  field="fileImage2"
                  number={2}
                  hideTags
                  originalFile
                />
              </Form.Item>
              <Form.Item
                name="name"
                label="Imię i nazwisko"
                rules={[
                  {
                    required: true,
                  },
                ]}
              >
                <Input />
              </Form.Item>
              <Form.Item
                name="phone"
                label="Numer telefonu"
                rules={[
                  {
                    required: true,
                  },
                ]}
              >
                <Input />
              </Form.Item>
              <Form.Item
                name="email"
                label="Adres e-mail"
                rules={[
                  {
                    required: true,
                  },
                ]}
              >
                <Input />
              </Form.Item>
              <Form.Item
                name="idProvince1"
                label="Województwo 1"
                rules={[
                  {
                    required: true,
                  },
                ]}
              >
                <Select
                  showSearch
                  placeholder="Wybierz województwo"
                  optionFilterProp="children"
                  options={provinces}
                  filterOption={(input, option) => {
                    // @ts-ignore
                    if (option?.label) {
                      // @ts-ignore
                      return option.label
                        .toLowerCase()
                        .includes(input.toLowerCase());
                    }
                    return false;
                  }}
                />
              </Form.Item>
              <Form.Item name="idProvince2" label="Województwo 2">
                <Select
                  showSearch
                  placeholder="Wybierz województwo"
                  optionFilterProp="children"
                  options={provinces}
                  allowClear
                  filterOption={(input, option) => {
                    // @ts-ignore
                    if (option?.label) {
                      // @ts-ignore
                      return option.label
                        .toLowerCase()
                        .includes(input.toLowerCase());
                    }
                    return false;
                  }}
                />
              </Form.Item>
              <Form.Item name="idProvince3" label="Województwo 3">
                <Select
                  showSearch
                  placeholder="Wybierz województwo"
                  optionFilterProp="children"
                  options={provinces}
                  allowClear
                  filterOption={(input, option) => {
                    // @ts-ignore
                    if (option?.label) {
                      // @ts-ignore
                      return option.label
                        .toLowerCase()
                        .includes(input.toLowerCase());
                    }
                    return false;
                  }}
                />
              </Form.Item>
              <Form.Item
                name="sortOrder"
                label="Kolejność"
                rules={[
                  {
                    required: true,
                  },
                  {
                    validator: async (_, value) => {
                      if (value) {
                        if (
                          (value.length > 5 ||
                            !/^[0-9]*\,?\.?[0-9]*$/.test(value)) &&
                          value.length > 0
                        ) {
                          return Promise.reject(
                            new Error('Proszę podać poprawną wartość!')
                          );
                        }
                      }
                    },
                  },
                ]}
              >
                <Input
                  style={{
                    width: '100px',
                  }}
                />
              </Form.Item>
              <Form.Item
                name="isNews"
                label="Twórca Wiadomości terenowych"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>
              <ActivitySwitch
                isActivityDisabled={isActivityDisabled}
                customText="Widoczny na podstronie Przedstawiciele terenowi"
              />
              <Form.Item>
                <Space>
                  <Button
                    type="primary"
                    htmlType="submit"
                    icon={<SaveOutlined />}
                    loading={isSaveButtonLoading}
                  >
                    Zapisz
                  </Button>
                  <Link to="/representatives">
                    <Button>Powrót</Button>
                  </Link>
                </Space>
              </Form.Item>
            </Form>
          )}
          {loading && (
            <Link to="/representatives">
              <Button>Powrót</Button>
            </Link>
          )}
          <BackTop />
        </div>
      </ConfigProvider>
    </>
  );
};

export default RepresentativesEdit;
